import { AbstractControl, FormArray, FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { Select } from '@corporate-tailwind/model';

// helpers types
type Constructor<T = any> = new () => T;
type Primitive = string | number | boolean | Date | null | undefined;
// type UnwrapArray<T> = T extends readonly (infer U)[] ? U : T;
type NonNullableDeep<T> = T extends null | undefined ? never : T;
type UnwrapArray<T> = NonNullableDeep<T> extends readonly (infer U)[]
  ? U
  : NonNullableDeep<T>;
type ModelNames<T, P extends string = ""> = {
  [K in keyof T]:
    NonNullableDeep<T[K]> extends Primitive
      ? {
          key: K & string;
          path: `${P}${K & string}`;
        }

      : NonNullableDeep<T[K]> extends readonly (infer U)[]
      ? {
          key: K & string;
          path: `${P}${K & string}`;
          isArray: true;
          fields: ModelNames<U, `${P}${K & string}.`>;
        }

      : {
          key: K & string;
          path: `${P}${K & string}`;
          fields: ModelNames<NonNullableDeep<T[K]>, `${P}${K & string}.`>;
        };
};

export class CorpForm<T> {
    public formGroup: FormGroup;
    public modelNames: ModelNames<T>;
    public isPatching: boolean = false;
    public onInit: boolean = true;

    constructor(
        public cls: Constructor<T>,
        clu: { schema: Record<string, any> }

        
        ){
          this.modelNames = {} as ModelNames<T>;
          this.buildModelNames(clu, this.modelNames);
          this.formGroup = this.createFormGroupFromSchema();

          
    }

/**
   * Parcours le schema et génère les chemins pour chaque propriété
   * @param cls Schema de la classe
   * @param target Objet où stocker le résultat
   * @param prefix Préfixe pour les chemins imbriqués (ex: "company")
   */


  protected buildModelNames(
  cls: { schema: Record<string, any> },
  target: any,
  prefix: string = ''
) {
  Object.keys(cls.schema).forEach((key) => {
    const field = cls.schema[key];
    const path = prefix ? `${prefix}.${key}` : key;

    const isPrimitive =
      field.type === String ||
      field.type === Number ||
      field.type === Boolean ||
      field.type === Date;

    // -----------------------------
    // base object
    // -----------------------------
    target[key] = {
      key,        // 🔥 IMPORTANT
      path
    };

    // -----------------------------
    // primitive
    // -----------------------------
    if (isPrimitive) {
      return;
    }

    // -----------------------------
    // FormArray
    // -----------------------------
    if (field.type === Array && field.itemType) {
      target[key].isArray = true;
      target[key].fields = {};

      if (field.itemType.schema) {
        this.buildModelNames(
          field.itemType,
          target[key].fields,
          path
        );
      }

      return;
    }

    // -----------------------------
    // objet imbriqué
    // -----------------------------
    if (field.type?.schema) {
      target[key].fields = {};

      this.buildModelNames(
        field.type,
        target[key].fields,
        path
      );
    }
  });
}


//   protected buildModelNames(
//   cls: { schema: Record<string, any> },
//   target: any,
//   prefix: string = ''
// ) {
//   Object.keys(cls.schema).forEach((key) => {
//     const field = cls.schema[key];
//     const path = prefix ? `${prefix}.${key}` : key;

//     const isPrimitive =
//       field.type === String ||
//       field.type === Number ||
//       field.type === Boolean ||
//       field.type === Date;

//     // -----------------------------
//     // ✅ PRIMITIVE
//     // -----------------------------
//     if (isPrimitive) {
//       target[key] = { path };
//       return;
//     }

//     // -----------------------------
//     // ✅ FORM ARRAY
//     // -----------------------------
//     if (field.type === Array && field.itemType) {
//       target[key] = {
//         path,
//         isArray: true,
//         fields: {}
//       };

//       // 🔥 descendre dans itemType.schema
//       if (field.itemType.schema) {
//         this.buildModelNames(
//           field.itemType,
//           target[key].fields,
//           `${path}[]`
//         );
//       }

//       return;
//     }

//     // -----------------------------
//     // ✅ OBJET COMPLEXE
//     // -----------------------------
//     if (field.type && field.type.schema) {
//       target[key] = {
//         path,
//         fields: {}
//       };

//       this.buildModelNames(
//         field.type,
//         target[key].fields,
//         path
//       );

//       return;
//     }

//     // -----------------------------
//     // fallback
//     // -----------------------------
//     target[key] = { path };
//   });
// }



/**
   * Transforme une lambda x => x.company.id en chemin string "company.id"
   * Fonctionne même si les propriétés sont null ou undefined.
   */
//simple:
// getPath(fn: string | string[]): string {
//   return Array.isArray(fn) ? fn.join('.') : fn;
// }

//complexe:
public getPath(fn: (x: T) => any): string {
  
  let expr = fn.toString();

  // 1. prendre partie après =>
  expr = expr.split('=>')[1];

  // 2. enlever ternaires complets
  expr = expr.replace(/.*\?[^:]+:\s*/, '');

  // 3. enlever "x." ou "t." ou n'importe quel param
  expr = expr.replace(/^\s*\w+\./, '');

  // 4. nettoyer espaces
  return expr.replace(/\s/g, '');
  
}

//trouver l'index non undefinded 
public findChangesIndex(objArray:any[]){

  const indexChanged= objArray.findIndex(el => el !== undefined);
  return indexChanged;

  // return objArray.map((item, index) => !this.isEmptyObject(item) ? index : -1)
  // .filter(index => index !== -1);
}

//isEmpty utilisé pour voir si changement dans les propriétés des formulaires dans valueChange
 isEmpty(value: any): boolean {
  // null ou undefined
  if (value === null || value === undefined) return true;

  // string vide
  if (typeof value === 'string') return value.trim().length === 0;

  // tableau
  if (Array.isArray(value)) {
    // vide OU contient uniquement des valeurs vides
    return value.length === 0 || value.every(item => this.isEmpty(item));
  }

  // objet
  if (typeof value === 'object') {
    return Object.keys(value).length === 0;
  }

  return false;
}

isEmptyArrayDeep(arr: any[]): boolean {
  if (!Array.isArray(arr)) return true;

  return arr.every(item => this.isEmpty(item));
}

getDiff(prev: any, curr: any): any {

  // =========================
  // STRICT EQUALITY
  // =========================
  if (prev === curr) {
    return undefined;
  }

  // =========================
  // NULL HANDLING
  // =========================
  if (prev === null || curr === null) {
    return curr;
  }

  // =========================
  // ARRAY ROOT
  // =========================
  if (Array.isArray(prev) && Array.isArray(curr)) {

    const arrayChanges: any[] = [];

    curr.forEach((item, index) => {

      const prevItem = prev[index];

      const itemDiff = this.getDiff(prevItem, item);

      if (itemDiff !== undefined) {

        arrayChanges.push({
          index,
          changes: itemDiff,
          current: item
        });
      }
    });

    return arrayChanges.length
      ? arrayChanges
      : undefined;
  }

  // =========================
  // OBJECT ROOT
  // =========================
  if (
    typeof prev === 'object' &&
    typeof curr === 'object'
  ) {

    const changes: any = {};

    Object.keys(curr).forEach(key => {

      const nestedDiff = this.getDiff(
        prev?.[key],
        curr?.[key]
      );

      if (nestedDiff !== undefined) {
        changes[key] = nestedDiff;
      }
    });

    return Object.keys(changes).length
      ? changes
      : undefined;
  }

  // =========================
  // PRIMITIVE
  // =========================
  return curr;
}

//V5
// getDiff(prev: any, curr: any): any {

//   // =========================
//   // ARRAY ROOT
//   // =========================
//   if (Array.isArray(prev) && Array.isArray(curr)) {

//     const arrayChanges: any[] = [];

//     curr.forEach((item, index) => {

//       const prevItem = prev[index];

//       // OBJECT
//       if (
//         typeof item === 'object' &&
//         typeof prevItem === 'object' &&
//         item !== null &&
//         prevItem !== null
//       ) {

//         const itemDiff = this.getDiff(prevItem, item);

//         // IMPORTANT:
//         // undefined = no diff
//         // null = valid diff
//         if (itemDiff !== undefined) {

//           arrayChanges.push({
//             index,
//             changes: itemDiff,
//             current: item
//           });
//         }

//         return;
//       }

//       // PRIMITIVE
//       if (item !== prevItem) {

//         arrayChanges.push({
//           index,
//           previous: prevItem,
//           current: item
//         });
//       }
//     });

//     return arrayChanges.length ? arrayChanges : undefined;
//   }

//   // =========================
//   // OBJECT ROOT
//   // =========================
//   if (
//     typeof prev === 'object' &&
//     typeof curr === 'object' &&
//     prev !== null &&
//     curr !== null
//   ) {

//     const changes: any = {};

//     Object.keys(curr).forEach(key => {

//       const prevVal = prev?.[key];
//       const currVal = curr?.[key];

//       // both undefined only
//       if (prevVal === undefined && currVal === undefined) {
//         return;
//       }

//       const nestedDiff = this.getDiff(prevVal, currVal);

//       // IMPORTANT:
//       // undefined = no diff
//       // null = valid diff
//       if (nestedDiff !== undefined) {
//         changes[key] = nestedDiff;
//       }
//     });

//     return Object.keys(changes).length
//       ? changes
//       : undefined;
//   }

//   // =========================
//   // PRIMITIVE
//   // =========================
//   if (prev !== curr) {
//     return curr;
//   }

//   return undefined;
// }

//v4
// getDiff(prev: any, curr: any): any {

//   // =========================
//   // ARRAY ROOT
//   // =========================
//   if (Array.isArray(prev) && Array.isArray(curr)) {

//     const arrayChanges: any[] = [];

//     curr.forEach((item, index) => {

//       const prevItem = prev[index];

//       // object
//       if (
//         typeof item === 'object' &&
//         typeof prevItem === 'object'
//       ) {

//         const itemDiff = this.getDiff(prevItem, item);

//         if (!this.isEmpty(itemDiff)) {
//           arrayChanges.push({
//             index,
//             changes: itemDiff,
//             current: item
//           });
//         }

//         return;
//       }

//       // primitive
//       if (item !== prevItem) {
//         arrayChanges.push({
//           index,
//           previous: prevItem,
//           current: item
//         });
//       }
//     });

//     return arrayChanges.length ? arrayChanges : undefined;
//   }

//   // =========================
//   // OBJECT ROOT
//   // =========================
//   if (
//     typeof prev === 'object' &&
//     typeof curr === 'object' &&
//     prev !== null &&
//     curr !== null
//   ) {

//     const changes: any = {};

//     Object.keys(curr).forEach(key => {

//       const prevVal = prev?.[key];
//       const currVal = curr?.[key];

//       // skip empty
//       if (this.isEmpty(prevVal) && this.isEmpty(currVal)) {
//         return;
//       }

//       const nestedDiff = this.getDiff(prevVal, currVal);

//       if (!this.isEmpty(nestedDiff)) {
//         changes[key] = nestedDiff;
//       }
//     });

//     return Object.keys(changes).length ? changes : undefined;
//   }

//   // =========================
//   // PRIMITIVE
//   // =========================
//   if (prev !== curr) {
//     return curr;
//   }

//   return undefined;
// }
//v3
// getDiff(prev: any, curr: any): any {
//   const changes: any = {};

//   Object.keys(curr).forEach(key => {
//     const prevVal = prev?.[key];
//     const currVal = curr?.[key];

//     if (this.isEmpty(prevVal) && this.isEmpty(currVal)) {
//       return;
//     }

//     // OBJECT
//     if (
//       typeof prevVal === 'object' &&
//       typeof currVal === 'object' &&
//       !Array.isArray(prevVal) &&
//       !Array.isArray(currVal)
//     ) {
//       const nestedDiff = this.getDiff(prevVal, currVal);

//       if (!this.isEmpty(nestedDiff)) {
//         changes[key] = nestedDiff;
//       }

//       return;
//     }

//     // ARRAY
//     if (Array.isArray(prevVal) && Array.isArray(currVal)) {
      
//       const arrayChanges: any[] = [];

//       currVal.forEach((item, index) => {
//         const prevItem = prevVal[index];

//         if (typeof item === 'object' && typeof prevItem === 'object') {

//           const itemDiff = this.getDiff(prevItem, item);

//           if (!this.isEmpty(itemDiff)) {

//             arrayChanges.push({
//               index,
//               changes: itemDiff,
//               current: item
//             });
//           }

//         } else if (item !== prevItem) {

//           arrayChanges.push({
//             index,
//             previous: prevItem,
//             current: item
//           });
//         }
//       });

//       if (arrayChanges.length > 0) {
//         changes[key] = arrayChanges;
//       }

//       return;
//     }

//     // PRIMITIVE
//     if (prevVal !== currVal) {
//       changes[key] = currVal;
//     }
//   });

//   return changes;
// }



//v2
// getDiff(prev: any, curr: any): any {
//   const changes: any = {};

//   Object.keys(curr).forEach(key => {
//     const prevVal = prev?.[key];
//     const currVal = curr?.[key];

//     // Si valeurs identiques ou toutes vides → pas de diff
//     if (this.isEmpty(prevVal) && this.isEmpty(currVal)) return;

//     // Objet imbriqué
//     if (typeof prevVal === 'object' && !Array.isArray(prevVal) &&
//         typeof currVal === 'object' && !Array.isArray(currVal)) {
//       const nestedDiff = this.getDiff(prevVal, currVal);
//       if (!this.isEmpty(nestedDiff)) {
//         changes[key] = nestedDiff;
//       }
//       return;
//     }

//     // Tableau
//     if (Array.isArray(prevVal) && Array.isArray(currVal)) {

//       const arrayDiff = currVal.map((item, index) => {
//         const prevItem = prevVal[index];
//         if (typeof item === 'object' && typeof prevItem === 'object') {
//           const diffItem = this.getDiff(prevItem, item);
//           return this.isEmpty(diffItem) ? undefined : diffItem;
//         }
//         return item !== prevItem ? item : undefined;
//       });

//       if (!arrayDiff.every(this.isEmpty)) {
//         changes[key] = arrayDiff;
//       }
//       return;
//     }

//     // Valeur simple
//     if (prevVal !== currVal) {
//       changes[key] = currVal;
//     }
//   });

//   return changes;
// }

//v1
// getDiff(prev: any, curr: any): any {
//   const changes: any = {};

//   Object.keys(curr).forEach(key => {
//     const prevVal = prev?.[key];
//     const currVal = curr?.[key];
    
//     // Objet imbriqué
//     if (this.isObject(prevVal) && this.isObject(currVal)) {
//       const nestedDiff = this.getDiff(prevVal, currVal);
//       if (Object.keys(nestedDiff).length > 0) {
//         // if(key=='targetAmount')
//         //   console.log(`${prevVal} | ${currVal}`);
//         // if(key=='sourceAmount')
//         //   console.log(`${prevVal} | ${currVal}`);
//         changes[key] = nestedDiff;
//       }
//     }
//     // Tableau : diff index par index
//     else if (Array.isArray(prevVal) && Array.isArray(currVal)) {
//       const arrayDiff: any[] = [];
//       currVal.forEach((item, index) => {
//         const prevItem = prevVal[index];
//         if (this.isObject(item) && this.isObject(prevItem)) {
//           const itemDiff = this.getDiff(prevItem, item);
//           arrayDiff[index] = itemDiff;
//         } else if (prevItem !== item) {
//           arrayDiff[index] = item;
//         } else {
//           arrayDiff[index] = undefined; // pas de changement
//         }
//       });

//       // Supprimer les undefined
//       if (!this.isEmptyArrayDeep(arrayDiff)) {
//         changes[key] = arrayDiff;
//       }
//     }
//     // Valeur simple
//     else if (prevVal !== currVal) {
//       // console.log(`valeur simple: ${prevVal} | ${currVal}`);
//       changes[key] = currVal;
//     }
//   });

//   return changes;
// }

 isObject(value: any): boolean {
  return value && typeof value === 'object' && !Array.isArray(value);
}



isEmptyObject(obj: any): boolean {
  return obj && typeof obj === 'object' && Object.keys(obj).length === 0;
}
//old:
  // public getPath(fn: (x: T) => any): string {
  //   let path = '';

  //   const makeProxy = (): any =>
  //     new Proxy(
  //       {},
  //       {
  //         get: (_, prop: string) => {
  //           path = path ? `${path}.${prop}` : prop;
  //           return makeProxy(); // nouveau proxy pour propriétés imbriquées
  //         }
  //       }
  //     );

  //   // Exécute la lambda sur le proxy
  //   fn(makeProxy() as any);
  //   return path;
  // }

  // public getValueFromHtml(path: string): any {
  //   // const path = this.getPath(fn);
  //   return this.formGroup.get(path)?.value;
  // }
getControlFromPath(path: string) {
  const normalizedPath = path.replace(/\[(\d+)\]/g, '.$1');
  return this.formGroup.get(normalizedPath);
}

  public getControl(fn: (x: T) => any): AbstractControl | null { 
    const path = this.getPath(fn);
    return this.formGroup.get(path);
  }

public getFormArray(fn: (x: T) => any): FormArray {

  let path = this.getPath(fn);
path = path.replace('?', '');

  const control = this.formGroup.get(path);

  if (!control) {
    throw new Error(`FormArray not found: ${path}`);
  }

  if (!(control instanceof FormArray)) {
    throw new Error(`Control is not a FormArray: ${path}`);
  }

  return control;
}

  /**
   * Récupère la valeur d'un FormControl correspondant à la lambda
   */
  public getValue(fn: (x: T) => any): any {

    let path = this.getPath(fn);
    path = path.replace('?', '');
    return this.formGroup.get(path)?.value;
  }

  public getArrayValue(
    fn: (x: T) => any,
    index: number
  ) {

    let path = this.getPath(fn);
    path = path.replace('?', '');
    const array = this.formGroup.get(path) as any; // FormArray

    if (!array) return null;

    return array.at(index);
  }

//   public getValue(path: string, index?: number, childPath?: string): any {
//   const control = this.formGroup.get(path);

//   if (!control) return null;

//   // Cas FormArray
//   if (Array.isArray(control.value) && index !== undefined) {
//     const array = control as any; // FormArray

//     const item = array.at(index);
//     if (!item) return null;

//     return childPath ? item.get(childPath)?.value : item.value;
//   }

//   // Cas FormGroup simple
//   return control.value;
// }

    /**
   * Modifie la valeur d'un FormControl correspondant à la lambda
   */
  public setValue(fn: (x: T) => any, value: any): void {
    let path = this.getPath(fn);
    path = path.replace(/\?/g, '');
    
    this.formGroup.get(path)?.setValue(value);
  }

  public setArrayValue(
    fn: (x: T) => any,
    index: number,
    field: string,
    value: any
  ): void {
    const path = this.getPath(fn).replace(/\?\./g, '.');

    const control = this.formGroup.get(path);

    if (!control || !(control instanceof FormArray)) {
      console.error('Invalid FormArray path:', path);
      return;
    }

    const item = control.at(index);

    if (!item) {
      console.error('Index out of bounds:', index);
      return;
    }

    const t =item.get(field);

    item.get(field)?.setValue(value);
  }

  public enableControl(fn: (x: T) => any): void {
    let path = this.getPath(fn);
    path = path.replace(/\?/g, '');

    const control = this.formGroup.get(path);

    control?.enable({ onlySelf: true, emitEvent: false });
    // return this.formGroup.get(path)?.value;
  }

  public disableControl(fn: (x: T) => any): void {
    let path = this.getPath(fn);
    path = path.replace(/\?/g, '');
    const control = this.formGroup.get(path);

    control?.disable({ onlySelf: true, emitEvent: false });
  }

  public disableForm(): void {
    this.disableFormGroup(this.formGroup);
  }
 
  public safePatchValue(value: any): void {
    this.isPatching = true;
    const schema = this.getSchema();

    if (!value) {
      this.formGroup.reset({}, { emitEvent: false });
      return;
    }

    this.patchFormGroup(this.formGroup, value, schema);
    this.isPatching = false;

    // console.log('formGroup', this.formGroup);
  }

  private patchFormGroup(
    group: FormGroup,
    value: any,
    schema: Record<string, any>
  ) {
    Object.keys(schema).forEach(key => {
      const entry = schema[key];
      const control = group.get(key);
      const val = value[key];

      if (!control || val === null || val === undefined) return;
      // if (!control || val === undefined) return;

      const { type, itemType } = entry;

      // -----------------------------
      // ✅ FormGroup imbriqué
      // -----------------------------
      if (control instanceof FormGroup && type?.schema) {
        this.patchFormGroup(control, val, type.schema);
        return;
      }

      // -----------------------------
      // ✅ FormArray
      // -----------------------------
      if (control instanceof FormArray && Array.isArray(val)) {
          val.forEach((item, index) => {
        let child = control.at(index);

        if (!child) {
          child = this.createFormGroup(itemType.schema);
          control.push(child);
        }

        this.patchFormGroup(child as FormGroup, item, itemType.schema);
      });

      // supprimer surplus
      while (control.length > val.length) {
        control.removeAt(control.length - 1);
      }

      return;
      }
      // -----------------------------
      // ✅ FormControl
      // -----------------------------
      control.patchValue(val, { emitEvent: false });
    });
  }



  //toutes les propriétes niveau 1 sont null --> le formulaire est vide
  public isFormEmpty(): boolean {
    const values = this.formGroup.value;

    return Object.keys(values).every(key => {
      const value = values[key];
      return value === null || value === undefined || value === '';
    });
  }


  private createFormGroupFromSchema(): FormGroup {
      const schema = this.getSchema();
      return this.createFormGroup(schema);
  }

  private getSchema(){
      const schema = (this.cls as any).schema;
      if (!schema) throw new Error('Missing static schema on class');

      return schema;
  }

  private disableFormGroup(formGroup: FormGroup): void {
      Object.keys(this.formGroup.controls).forEach(key => {
      const control = this.formGroup.controls[key];

      if (control instanceof FormControl) {
        control.disable({ onlySelf: true, emitEvent: false });
      } else if (control instanceof FormGroup) {
        // Appel récursif pour les sous-FormGroup
        this.disableFormGroup(control);
      } else if (control instanceof FormArray) {
        // Pour FormArray, on peut désactiver chaque élément
        control.controls.forEach(ctrl => {
          if (ctrl instanceof FormControl) {
            ctrl.disable({ onlySelf: true, emitEvent: false });
          } else if (ctrl instanceof FormGroup) {
            this.disableFormGroup(ctrl);
          }
        });
      }
    });
  }
    
  // utilitaire pour détecter "constructeurs primitifs"
  private isPrimitiveCtor(fn: Function): boolean {
      return [String, Number, Boolean, Date].includes(fn as any);
  }

  // private createFormGroup(schema: Record<string, any>): FormGroup {
  //   const formGroup = new FormGroup({});

  //   for (const [key, entry] of Object.entries(schema)) {
  //     const { type, builderOption } = entry;

  //     // -----------------------------
  //     // Cas 1 : Champs primitifs ou option form
  //     // -----------------------------
  //     if (this.isFormOption(builderOption)) {
  //       const control = new FormControl(
  //         builderOption.defaultValue ?? this.getDefaultValue(type),
  //         (builderOption.validators as ValidatorFn[]) ?? []
  //       );


  //     const pp = control?.validator;
  //     if (pp) {
  //       const test = pp({} as any);
  //       const hasRequired = test && test['required'];

  //     }


  //     if (builderOption.defaultDisabled) {
  //       control.disable({ onlySelf: true, emitEvent: false });
  //     }

  //       formGroup.addControl(key, control);
  //       continue;
  //     }

  //     // -----------------------------
  //     // Cas 2 : Champs complexes (type avec schema)
  //     // -----------------------------
  //     if (type && (type as any).schema) {
  //       formGroup.addControl(key, this.createFormGroup((type as any).schema));
  //       continue;
  //     }

  //     // -----------------------------
  //     // Cas 3 : Champs complexes (type avec schema)
  //     // -----------------------------
  //     if (type === Array && entry.itemType) {
  //       formGroup.addControl(key, new FormArray([]));
  //       continue;
  //     }

  //     // -----------------------------
  //     // Cas 4 : fallback pour champs simples sans builderOption
  //     // -----------------------------
  //     formGroup.addControl(key, new FormControl(this.getDefaultValue(type)));
  //   }

  //   return formGroup;
  // }

  private createFormGroup(schema: Record<string, any>): FormGroup {
  const formGroup = new FormGroup({});

  for (const [key, entry] of Object.entries(schema)) {
    const { type, builderOption, itemType } = entry;

    // -----------------------------
    // 🔥 PRIORITÉ ABSOLUE : Select → FormControl
    // -----------------------------
    if (this.isSelect(type)) {

      const control = new FormControl(
        builderOption?.defaultValue ?? this.getDefaultValue(type),
        (builderOption?.validators as ValidatorFn[]) ?? []
      );

      formGroup.addControl(key, control);
      continue;
    }

    // -----------------------------
    // ✅ Cas 1 : objets imbriqués (PRIORITAIRE)
    // -----------------------------
    if (type && (type as any).schema) {
      formGroup.addControl(
        key,
        this.createFormGroup((type as any).schema)
      );
      continue;
    }

    // -----------------------------
    // ✅ Cas 2 : tableaux
    // -----------------------------
    if (type === Array && itemType) {
      formGroup.addControl(key, new FormArray([]));
      continue;
    }

    // -----------------------------
    // Cas 3 : FormControl
    // -----------------------------
    if (this.isFormOption(builderOption)) {
        
        const control = new FormControl(
        builderOption.defaultValue ?? this.getDefaultValue(type),
        {
          validators:(builderOption.validators as ValidatorFn[]) ?? [],
          updateOn: (builderOption.updateOn as 'change' | 'blur' | 'submit') ?? 'change'
        }
      );

      if (builderOption.defaultDisabled) {
        control.disable({ onlySelf: true, emitEvent: false });
      }

      formGroup.addControl(key, control);
      continue;
    }

    // -----------------------------
    // fallback
    // -----------------------------
    formGroup.addControl(
      key,
      new FormControl(this.getDefaultValue(type))
    );
  }

  return formGroup;
}

private isSelect(type: any): boolean {
  return type?.prototype instanceof Select || type?.name === 'Select';
}

// private isSelect(type: any): boolean {
//   if (!type) return false;

//   let current = type;

//   while (current) {
//     if (current.name === 'Select') {
//       return true;
//     }

//     current = Object.getPrototypeOf(current);
//   }

//   return false;
// }

  private isFormOption(value: any): boolean {
    return value && typeof value === 'object' && (
      'validators' in value ||
      'defaultValue' in value ||
      'defaultDisabled' in value
    );
  }

  private getDefaultValue(type: Function): any {
    switch (type) {
      case String: return '';
      case Number: return 0;
      case Boolean: return false;
      case Date: return null;
      default: return null;
    }
  }

}
